import './App.css';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiLayers, FiPlay, FiSliders, FiZap } from 'react-icons/fi';
import { ReadyToStart } from './main-side/ReadyToStart';
import { Template } from './Template';

const demos = [
  { videoId: 'Dp9qtjZmle4', title: 'Create supports', description: 'Watch Slick create supports for a 3DBenchy model.' },
  { videoId: '_cPZv1w3MSM', title: 'Slice geometric figures', description: 'Watch geometric figures being sliced in Slick.' },
  { videoId: 'w_vWwbIUPOk', title: 'Slice 3DBenchy', description: 'Watch Slick slice a 3DBenchy model.' },
];

function App() {
  const [activeDemo, setActiveDemo] = useState(1);
  const { videoId: demoVideoId, title: demoTitle, description: demoDescription } = demos[activeDemo - 1];

  return (
    <Template>
      <section className="hero page-width">
        <div className="hero-copy">
          <span className="eyebrow"><span className="status-dot" /> IN DEVELOPMENT · BUILT FOR RESIN</span>
          <h1>Resin slicing.<br /><span>Seriously fast.</span></h1>
          <p>Slick slices models <strong>3 times faster than other popular resin slicers.</strong> We're a small team putting a lot of care into Slick, and we need the community's advice, feedback, and support to help it grow.</p>
          <div className="hero-actions"><Link className="button button-primary" to="/download">Download Slick <FiArrowUpRight aria-hidden="true" /></Link><a className="button button-secondary" href="#demo">Watch Slick in action <span aria-hidden="true">↓</span></a></div>
          <div className="hero-note"><span className="status-dot" /> Still in development. Your ideas can help shape it.</div>
        </div>
        <div className="slice-art" role="img" aria-label="Illustration of a sculptural model separated into layers on a resin printer build plate">
          <div className="art-top"><span><span className="status-dot" /> THE ART OF PRECISION</span><span>001 / SLICK</span></div>
          <svg viewBox="0 0 560 530" aria-hidden="true">
            <defs><linearGradient id="slice-fill" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#a9b5ff" /><stop offset=".5" stopColor="#6b7ef1" /><stop offset="1" stopColor="#5b6ee1" /></linearGradient><pattern id="grid" width="36" height="36" patternUnits="userSpaceOnUse"><path d="M 36 0 L 0 0 0 36" fill="none" stroke="#a5b1f7" strokeOpacity=".13" /></pattern></defs>
            <path d="M40 379 279 265 525 379 285 503Z" fill="url(#grid)" stroke="#6573b2" strokeOpacity=".5" />
            <ellipse cx="280" cy="389" rx="139" ry="41" fill="#6b7ef1" opacity=".06" />
            {Array.from({ length: 30 }, (_, i) => {
              const width = 72 + 39 * Math.sin(i / 5);
              const y = 365 - i * 8;
              const x = 280 + 28 * Math.sin(i / 7);
              return <ellipse key={i} cx={x} cy={y} rx={width} ry={width * .38} fill="url(#slice-fill)" stroke="#252e57" strokeWidth="1.5" />;
            })}
            <path d="M350 169H443V128M185 338H105V369" fill="none" stroke="#6b7ef1" strokeOpacity=".6" /><circle cx="350" cy="169" r="3" fill="#6b7ef1" />
            <text x="412" y="114" fill="#9ca3bf" fontSize="11" fontFamily="monospace">LAYER BY LAYER</text>
            <text x="65" y="392" fill="#9ca3bf" fontSize="11" fontFamily="monospace">MAKE IT REAL</text>
          </svg>
          <div className="art-bottom"><span>RESIN PRINTING, REFINED.</span><span className="art-coordinate">X / Y / Z <span>↗</span></span></div>
        </div>
      </section>
      <section className="demo-section page-width" id="demo" aria-labelledby="demo-title">
        <div className="section-heading">
          <span className="eyebrow"><span className="status-dot" /> A LOOK INSIDE SLICK</span>
          <h2 id="demo-title">See Slick<br />in action.</h2>
          <p>Here's what we've been working on. Watch Slick in action, then tell us what you think and what would make it better for you.</p>
        </div>
        <div className="demo-selector" role="group" aria-label="Choose a Slick demo">
          {demos.map((demo, index) => (
            <button
              key={demo.videoId}
              type="button"
              className={`demo-option${activeDemo === index + 1 ? ' is-active' : ''}`}
              aria-pressed={activeDemo === index + 1}
              aria-controls="demo-player"
              onClick={() => setActiveDemo(index + 1)}
            >
              <span className="demo-option-icon"><FiPlay aria-hidden="true" /></span>
              <span className="demo-option-copy">
                <span className="demo-option-number">DEMO 0{index + 1}</span>
                <span className="demo-option-title">{demo.title}</span>
              </span>
              <span className="demo-option-status" aria-hidden="true">{activeDemo === index + 1 ? 'SELECTED' : 'WATCH DEMO'}</span>
            </button>
          ))}
        </div>
        <figure className="demo-player" id="demo-player">
          <div className="demo-toolbar" aria-hidden="true">
            <span className="demo-window-dots"><i /><i /><i /></span>
            <span>SLICK / DEMO 0{activeDemo}</span>
            <span className="demo-preview">DEVELOPMENT PREVIEW</span>
          </div>
          <iframe
            key={demoVideoId}
            src={`https://www.youtube-nocookie.com/embed/${demoVideoId}?rel=0&playsinline=1`}
            title={`Slick demo ${activeDemo}: ${demoTitle}`}
            aria-describedby="demo-caption"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          <figcaption id="demo-caption">
            <div className="demo-caption-summary">
              <span>{demoDescription}</span>
              <a className="demo-youtube-link" href={`https://youtu.be/${demoVideoId}`} target="_blank" rel="noopener noreferrer">Watch on YouTube <FiArrowUpRight aria-hidden="true" /></a>
            </div>
          </figcaption>
        </figure>
      </section>
      <section className="feature-section page-width" id="features">
        <div className="section-heading"><span className="eyebrow">WHAT WE'RE BUILDING</span><h2>Fast slicing.<br />Shaped by you.</h2><p>Our focus is simple: a fast slicer for resin printing, developed with help from the people who use it.</p></div>
        <div className="feature-grid">
          <article className="feature-card"><FiZap aria-hidden="true" /><span className="feature-number">01</span><h3>Built for speed</h3><p>A lightweight Rust core and GPU acceleration designed to keep your workflow moving.</p></article>
          <article className="feature-card"><FiLayers aria-hidden="true" /><span className="feature-number">02</span><h3>Every layer matters</h3><p>A dedicated resin slicing workflow, built around the details that bring your models to life.</p></article>
          <article className="feature-card"><FiSliders aria-hidden="true" /><span className="feature-number">03</span><h3>Your experience matters</h3><p>We're a small team, and your advice helps us decide what to improve. Tell us what slows you down and what you need from your slicer.</p></article>
        </div>
      </section>
      <ReadyToStart />
    </Template>
  );
}
export default App;
